import { UseQueryOptions, UseQueryResult } from '@tanstack/react-query';
import { DefaultResponse } from '@vindr/services/interfaces';
import isEmpty from 'lodash/isEmpty';
import { useMemo } from 'react';
import { Omit } from '../helpers/common.helper';

/**
 * Common hook for querying and merging data based on dynamic search parameters.
 *
 * @param queryFn - The React Query hook to fetch data.
 * @param defaultParams: based parameters for query data.
 * @param queryParams - Search by Backend parameters for query data.
 * @param searchParams - Search by Frontend by initial list of query data.
 * @returns An object containing the merged data, loading state, and error.
 */
function useMergedQuery<P, D>({
  queryFn,
  defaultParams,
  queryParams = {} as P,
  searchParams = {},
  options,
}: useMergedQueryProps<P, D>) {
  const isEnabled = options?.enabled ?? true;
  const enabledInitialQuery = !isEmpty(searchParams) && isEnabled;

  const initialQuery = queryFn(defaultParams, {
    enabled: enabledInitialQuery,
    ...Omit(options, ['enabled']),
  });

  const initialData: D[] = useMemo(
    () => (enabledInitialQuery ? initialQuery.data?.data || [] : []),
    [enabledInitialQuery, initialQuery.data?.data]
  );

  // Determine missing search parameters
  const filteredData: D[] = useMemo(() => {
    return initialData.filter((item) =>
      Object.entries(searchParams).every(([key, value]) => {
        const itemValue = item[key as keyof D];
        if (Array.isArray(value)) {
          return value.includes(itemValue);
        }
        return itemValue === value;
      })
    );
  }, [initialData, searchParams]);

  const hasMissingData: boolean = useMemo(() => {
    if (isEmpty(searchParams)) return true; // Want to search detail, skip search list
    if (initialData.length === 0) return enabledInitialQuery; // No data to compare, possibly an error API || not enabled

    for (const [key, value] of Object.entries(searchParams)) {
      const itemValues = initialData.map((item) => item[key as keyof D]);

      if (Array.isArray(value)) {
        if (value.some((v) => !itemValues.includes(v))) return true;
      } else if (!itemValues.includes(value)) return true;
    }

    return false;
  }, [enabledInitialQuery, initialData, searchParams]);

  const enabledMissingQuery =
    hasMissingData && !isEmpty(queryParams) && isEnabled;
  const missingQuery = queryFn(
    { ...defaultParams, ...queryParams },
    {
      enabled: enabledMissingQuery,
      ...Omit(options, ['enabled']),
    }
  );
  const missingData: D[] = useMemo(
    () => (enabledMissingQuery ? missingQuery.data?.data || [] : []),
    [enabledMissingQuery, missingQuery.data?.data]
  );

  // Merge data from initial and missing queries
  const mergedDataArray: D[] = useMemo(() => {
    if (filteredData.length === 0) return missingData;
    if (missingData.length === 0) return filteredData;

    const mergedArray = [...filteredData];
    const mergedSet = new Set(mergedArray.map((item) => item?.id));

    missingData.forEach((item) => {
      if (item?.id && !mergedSet.has(item?.id)) {
        mergedArray.push(item);
        mergedSet.add(item?.id);
      }
    });

    return mergedArray;
  }, [filteredData, missingData]);

  // Determine loading state and error
  const isLoading =
    (enabledInitialQuery && initialQuery.isLoading) ||
    (enabledMissingQuery && missingQuery.isLoading);
  const error = initialQuery.error || missingQuery.error;

  return {
    data: mergedDataArray,
    isLoading,
    error,
  };
}

export default useMergedQuery;

interface useMergedQueryCommonProps<P, D> {
  queryFn: (
    params: P,
    options?: UseQueryOptions
  ) => UseQueryResult<DefaultResponse<D[]>>;
  defaultParams: P;
  options?: UseQueryOptions;
}

export type MergedQueryRequireSearchParams<P, D> = useMergedQueryCommonProps<
  P,
  D
> & {
  queryParams?: P;
  searchParams: Partial<D>;
};

export type MergedQueryRequireQueryParams<P, D> = useMergedQueryCommonProps<
  P,
  D
> & {
  queryParams: P;
  searchParams?: Partial<D>;
};

export type useMergedQueryProps<P, D> =
  | MergedQueryRequireSearchParams<P, D>
  | MergedQueryRequireQueryParams<P, D>;
